import classNames from 'classnames'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useMyMonitoringGetControlSelectListWithState } from '@apps-common/apis/my-monitoring/control/get-control-select-list-with-state'
import {
  useMyMonitoringCreateUnitSchedule,
  useMyMonitoringGetUnitScheduled,
  useMyMonitoringUpdateUnitScheduled
} from '@apps-common/apis/my-monitoring/schedules'
import { useGetUnitControlSelectList } from '@apps-common/apis/portal/control'
import { useCreateUnitScheduled, useGetUnitScheduled, useUpdateUnitScheduled } from '@apps-common/apis/portal/schedules'
import { CardContainer } from '@apps-common/components/common/CardContainer'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { VertSpacerLg, VertSpacerSm } from '@apps-common/components/layout'
import { CONTROL_COMMAND_SELECT_OPTIONS, CONTROL_STATE_ON, CONTROL_TRIGGER_TYPE_LATCH } from '@apps-common/config/controls'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { DAY_OPTIONS, TIME_OPTIONS } from '@apps-common/config/schedules'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { IUnitScheduledModel } from '@apps-common/types/portal/unit-scheduled'

const variants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.15
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.15
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15
    }
  }
}

export const FORM_SCHEDULE_MODIFY = 'schedule-modify-form'

export interface IProps {
  loading?: boolean
  unitId: string
  productId: string
  scheduleId?: number
  onSave: () => void
  onLoading: (value: boolean) => void
  onUpdating: (value: boolean) => void
  showSubmit?: boolean
}

export const ScheduleModifyForm: React.FC<IProps> = ({
  loading,
  unitId,
  productId,
  scheduleId,
  onSave,
  onLoading,
  onUpdating,
  showSubmit = true
}) => {
  const isEditing = scheduleId !== undefined

  const dotBorderColors = useProjectHook('border-brand', 'border-ctx-mm-brand')
  const dotColors = useProjectHook('bg-brand', 'bg-ctx-mm-brand')

  const useControlSelectHook = useProjectHook(useGetUnitControlSelectList, useMyMonitoringGetControlSelectListWithState)
  const useGetScheduleHook = useProjectHook(useGetUnitScheduled, useMyMonitoringGetUnitScheduled)
  const useCreateScheduleHook = useProjectHook(useCreateUnitScheduled, useMyMonitoringCreateUnitSchedule)
  const useUpdateScheduleHook = useProjectHook(useUpdateUnitScheduled, useMyMonitoringUpdateUnitScheduled)

  const { data: controls, isLoading: controlsIsLoading, isError: controlsIsError } = useControlSelectHook({ config: {}, unitId })

  const {
    data: schedule,
    isLoading: scheduleIsLoading,
    isRefetching: scheduleRefetching,
    isError: scheduleIsError
  } = useGetScheduleHook({ config: { enabled: isEditing }, scheduledId: scheduleId || 0 })

  const { mutate: createSchedule, isPending: createIsPending } = useCreateScheduleHook(true, () => onSave())
  const { mutate: updateSchedule, isPending: updateIsPending } = useUpdateScheduleHook(true, () => onSave())

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    watch,
    setValue,
    reset
  } = useForm<IUnitScheduledModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const registerDaysOfWeek = () => {
    register('daysOfWeek', {
      required: {
        value: true,
        message: 'At least 1 day must be selected'
      }
    })
  }

  useEffect(() => {
    registerDaysOfWeek()
  }, [])

  useEffect(() => {
    if (schedule) {
      reset(schedule)
      registerDaysOfWeek()
    }
  }, [schedule])

  const submit = (values: IUnitScheduledModel) => {
    if (values?.unitScheduledId) {
      updateSchedule({ model: values })
    } else {
      createSchedule({ model: values })
    }
  }

  const selectedDaysOfWeek = watch('daysOfWeek')
  const selectedControlId = watch('unitControlId')

  const getStateOptions = () => {
    const control = controls?.find((c: any) => c.unitControlId === selectedControlId)

    if (control && !control.requiresControlState && control.triggerType !== CONTROL_TRIGGER_TYPE_LATCH.value) {
      setValue('desiredState', CONTROL_STATE_ON)

      return CONTROL_COMMAND_SELECT_OPTIONS.filter((o) => o.value === CONTROL_STATE_ON)
    }

    return CONTROL_COMMAND_SELECT_OPTIONS
  }

  const isLoading = loading || scheduleIsLoading || scheduleRefetching || scheduleIsError || controlsIsLoading || controlsIsError
  const isUpdating = isSubmitting || createIsPending || updateIsPending

  useEffect(() => {
    onLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    onUpdating(isUpdating)
  }, [isUpdating])

  return (
    // <ProductNotSupportedCard
    //   bgClasses='bg-surface text-text border border-border'
    //   productId={productId}
    //   message='This product does not support schedules.'
    //   allowedProductIds={CONTROL_TRIGGER_SCHEDULE_ALLOWED_PRODUCTS}
    // >
    <form id={FORM_SCHEDULE_MODIFY} noValidate onSubmit={handleSubmit(submit)}>
      <div className='flex flex-col gap-[30px] max-w-[600px]'>
        <CardContainer bgClasses='bg-surface border border-border'>
          <div>
            <InputSM
              key='scheduledName'
              type='text'
              placeholder='Name to identify schedule'
              label='Schedule name'
              id='scheduledName'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('scheduledName', {
                required: FORM_DEFAULT_REQUIRED,
                maxLength: {
                  message: 'No longer than 25 characters',
                  value: 25
                }
              })}
              errors={errors.scheduledName}
            />
            <SelectInputSM
              key='unitControlId'
              type='text'
              placeholder='Control to schedule'
              label='Control'
              id='unitControlId'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('unitControlId', {
                required: FORM_DEFAULT_REQUIRED,
                valueAsNumber: true
              })}
              options={controls?.map((c: any) => ({ label: c.controlName, value: c.unitControlId })) || []}
              errors={errors.unitControlId}
            />
            <SelectInputSM
              key='desiredState'
              type='text'
              placeholder='desired state of control'
              label='Desired state'
              id='desiredState'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('desiredState', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={getStateOptions()}
              errors={errors.desiredState}
            />
            {isLoading && <div className={classNames('block ml-2 mb-3 h-[16px] bg-surface-2 max-w-[20%] rounded-lg')} />}
            {!isLoading && <label className='block pl-2 pb-2 text-sm text-text font-bold'>Days to run</label>}
            <div className='grid grid-cols-4 sm:grid-cols-7 gap-[10px]'>
              {isLoading &&
                DAY_OPTIONS.map((d, i) => (
                  <div key={`day-option-button-skeleton-${i}`} className='animate-pulse bg-surface-2 pt-[100%] rounded-3xl' />
                ))}
              {!isLoading &&
                DAY_OPTIONS.map((d, i) => {
                  const selected = selectedDaysOfWeek?.split(',')?.includes(d.value?.toString())

                  return (
                    <button
                      key={`day-option-button-${i}`}
                      type='button'
                      className={classNames(
                        'border border-border bg-surface-2 text-text jost-light grow flex flex-col items-center justify-center gap-1 p-3 rounded-3xl',
                        isUpdating && 'opacity-30'
                      )}
                      onClick={() => {
                        if (isUpdating) return

                        let values = selectedDaysOfWeek?.split(',') || []
                        const valueStr = d.value.toString()

                        if (!selected && !values.includes(valueStr)) {
                          values.push(valueStr)
                          values.sort()
                        } else {
                          values = values.filter((v) => v !== valueStr)
                        }
                        setValue('daysOfWeek', values.length > 0 ? values.join(',') : (undefined as any), { shouldValidate: true })
                      }}
                    >
                      <p className='text-[0.8em] jost-medium opacity-70'>{d.label}</p>
                      <div className={classNames(dotBorderColors, 'border w-[14px] h-[14px] rounded-[7px] p-[1px]')}>
                        <AnimatePresence mode='wait'>
                          {selected && (
                            <motion.div
                              key={`${d.value}-selected`}
                              variants={variants}
                              initial='initial'
                              animate='animate'
                              exit='exit'
                              className={classNames('w-[10px] h-[10px] rounded-[6px]', dotColors)}
                            />
                          )}
                          {!selected && (
                            <motion.div
                              key={`${d.value}-not-selected`}
                              variants={variants}
                              initial='initial'
                              animate='animate'
                              exit='exit'
                              className='w-[10px] h-[10px] rounded-[6px] bg-surface-2'
                            />
                          )}
                        </AnimatePresence>
                      </div>
                    </button>
                  )
                })}
            </div>
            <p className='pl-2 pt-2 text-error text-xs'>{errors?.daysOfWeek?.message}</p>
            <VertSpacerSm />
            <SelectInputSM
              key='timeToRun'
              type='text'
              placeholder='Time which schedule runs'
              label='Time to run'
              id='timeToRun'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('timeToRun', {
                required: {
                  value: true,
                  message: 'Required'
                }
              })}
              options={TIME_OPTIONS}
              errors={errors.timeToRun}
            />
            <CheckboxInputSM
              label='Is active'
              description='Whether the schedule is active and should run at the specified days/times.'
              id='active'
              loading={isLoading}
              disabled={isLoading || isUpdating}
              registration={register('active')}
              errors={errors.active}
            />
          </div>
        </CardContainer>
      </div>
      <VertSpacerLg />
      {showSubmit && (
        <ButtonRoundedSm
          type='submit'
          loading={isLoading || isUpdating}
          text={isEditing ? 'Update' : 'Create'}
          className='action-success'
        />
      )}
    </form>
    // </ProductNotSupportedCard>
  )
}
